import { motion } from "framer-motion";
import {
  Award,
  BarChart3,
  Check,
  Clock,
  HeadphonesIcon,
  Shield,
  Star,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchIPData } from "../../../Redux/Redux-slices/ipDataSlice";
import { plansINR, plansUSD } from "../../../utils/pricingPlans";
import SalesData from "../Sales/SalesData";
import ComparisonTable from "./ComparisonTable";

export default function PricingPlans() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currency, setCurrency] = useState("INR");
  const [showModalSales, setShowModalSales] = useState(false);

  useEffect(() => {
    if (showModalSales) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showModalSales]);

  const closeModalSale = () => {
    setShowModalSales(false);
  };

  const dispatch = useDispatch();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(fetchIPData()).then((action) => {
      const fetchedData = action.payload.data;
      if (fetchedData?.country_code && fetchedData.country_code !== "IN") {
        setCurrency("USD");
      }
    });
  }, [dispatch]);

  const plans = currency === "INR" ? plansINR : plansUSD;

  // Schema markup for pricing plans
  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "SelfSync AI-Powered Talent Assessment Platform",
    description:
      "Comprehensive talent assessment platform with AI-powered insights, personality profiling, and behavioral analysis for better hiring decisions.",
    brand: {
      "@type": "Brand",
      name: "SelfSync",
    },
    offers: plans.map((plan) => ({
      "@type": "Offer",
      name: plan.title,
      description: plan.description ? plan.description.join(", ") : plan.title,
      price: plan.price.replace(/[^\d]/g, "") || "0",
      priceCurrency: currency,
      availability: "https://schema.org/InStock",
      url: "https://selfsync.ai/sign-up",
      seller: {
        "@type": "Organization",
        name: "SelfSync",
      },
    })),
  };

  const handleClick = (buttonLabel) => {
    if (buttonLabel === "Contact Sales") {
      navigate("/contact-us");
    } else {
      window.open("https://portal.selfsync.ai/sign-up", "_blank");
    }
  };

  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "99% Accuracy Rate",
      description:
        "AI-powered personality assessments with industry-leading precision",
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Real-Time Analytics",
      description: "Instant insights into team performance and hiring patterns",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Enterprise Security",
      description: "Bank-level security with GDPR and SOC2 compliance",
    },
    {
      icon: <HeadphonesIcon className="w-6 h-6" />,
      title: "Expert Support",
      description: "Dedicated customer success team to maximize your ROI",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          SelfSync Pricing | AI Hiring Platform - Plans Starting Free
        </title>
        <meta
          name="description"
          content="Choose the perfect SelfSync plan for your team. Start free with 2 assessments. Transparent pricing with 99% accurate AI hiring insights. No hidden fees."
        />
        <meta
          name="keywords"
          content="selfsync pricing, AI hiring cost, talent assessment pricing, personality test pricing, hiring platform plans"
        />
        <link rel="canonical" href="https://selfsync.ai/pricing" />

        {/* Open Graph tags */}
        <meta
          property="og:title"
          content="SelfSync Pricing | AI Hiring Platform - Plans Starting Free"
        />
        <meta
          property="og:description"
          content="Choose the perfect SelfSync plan for your team. Start free with 2 assessments. 99% accurate AI hiring insights."
        />
        <meta property="og:url" content="https://selfsync.ai/pricing" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://selfsync.ai/static/media/SelfSync%20Logo.df7570a5489657df44eb.png"
        />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="SelfSync Pricing | AI Hiring Platform - Plans Starting Free"
        />
        <meta
          name="twitter:description"
          content="Choose the perfect SelfSync plan for your team. Start free with 2 assessments."
        />

        {/* Schema markup */}
        <script type="application/ld+json">
          {JSON.stringify(pricingSchema)}
        </script>
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-purple-600 to-cyan-600 text-white py-12 pt-28 max-md:py-12 max-md:pt-24">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4"
            >
              <span className="bg-yellow-400 text-purple-900 px-4 py-2 rounded-full text-xs font-bold">
                🚀 Launch Special: First 50 Assessments FREE
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-6xl font-bold mb-6"
            >
              Transparent Pricing,
              <br />
              <span className="text-yellow-400">Powerful Results</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl mb-8 max-w-3xl mx-auto"
            >
              Start free with 2 assessments. Scale with confidence. No hidden
              fees, no long-term contracts. Get 99% accurate hiring insights
              instantly.
            </motion.p>

            {/* Currency Toggle */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="relative inline-flex p-1 bg-white/20 backdrop-blur-sm rounded-full mb-8"
            >
              <div
                className={`absolute inset-y-0 my-1 bg-white rounded-full transition-all duration-300 ease-in-out ${
                  currency === "INR" ? "left-1 w-1/2" : "left-1/2 w-1/2"
                }`}
                style={{ height: "calc(100% - 0.5rem)" }}
              />

              <button
                className={`relative px-6 py-2 text-sm font-medium rounded-full z-10 transition-colors duration-200 ${
                  currency === "INR"
                    ? "text-purple-600"
                    : "text-white hover:text-yellow-400"
                }`}
                onClick={() => setCurrency("INR")}
              >
                ₹ INR
              </button>
              <button
                className={`relative px-6 py-2 text-sm font-medium rounded-full z-10 transition-colors duration-200 ${
                  currency === "USD"
                    ? "text-purple-600"
                    : "text-white hover:text-yellow-400"
                }`}
                onClick={() => setCurrency("USD")}
              >
                $ USD
              </button>
            </motion.div>

            {/* Trust Indicators */}
            <div className="flex items-center justify-center gap-8 text-sm">
              <span className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Enterprise Security
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                15-Min Setup
              </span>
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                260+ Companies
              </span>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-20 max-md:py-16 -mt-10">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 px-4 sm:px-6 ">
              {plans.map((plan, idx) => (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  key={idx}
                  className={`relative flex flex-col h-full rounded-2xl p-6 transition-all duration-300 hover:scale-105 ${
                    plan.highlighted
                      ? "bg-gradient-to-br from-purple-600 to-cyan-600 text-white shadow-2xl ring-4 ring-purple-200"
                      : "bg-white shadow-lg border border-gray-200 hover:shadow-xl"
                  }`}
                >
                  {(plan.popular || plan.highlighted) && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-yellow-400 text-purple-900 px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                        {plan.popular ? (
                          <Star className="w-3 h-3" />
                        ) : (
                          <Award className="w-3 h-3" />
                        )}
                        {plan.popular ? "Most Popular" : "Best Value"}
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <h3
                      className={`text-lg font-bold mb-2 ${
                        plan.highlighted ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {plan.title}
                    </h3>

                    <div className="mb-2">
                      <span
                        className={`text-xl font-bold ${
                          plan.highlighted ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {plan.price}
                      </span>
                      {plan.price !== "Free" &&
                        plan.price !== "Custom" && (
                          <span
                            className={`text-base ml-1 ${
                              plan.highlighted
                                ? "text-white/80"
                                : "text-gray-500"
                            }`}
                          >
                            {currency}
                          </span>
                        )}
                    </div>

                    {plan.subtext && (
                      <p
                        className={`text-sm ${
                          plan.highlighted ? "text-white/80" : "text-gray-500"
                        }`}
                      >
                        {plan.subtext}
                      </p>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.description.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check
                          className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                            plan.highlighted
                              ? "text-yellow-400"
                              : "text-green-600"
                          }`}
                        />
                        <span
                          className={`text-sm ${
                            plan.highlighted ? "text-white" : "text-gray-600"
                          }`}
                        >
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleClick(plan.button)}
                    className={`w-full py-2 px-2 rounded-lg font-semibold transition-all duration-200 mt-auto ${
                      plan.highlighted
                        ? "bg-white text-purple-600 hover:bg-gray-50 shadow-lg"
                        : "bg-gradient-to-r from-purple-600 to-cyan-600 text-white hover:from-purple-700 hover:to-cyan-700 shadow-lg hover:shadow-xl"
                    }`}
                  >
                    {plan.button}
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-xl md:text-4xl font-bold text-gray-900 mb-4">
                Why 260+ Companies Choose SelfSync
              </h2>
              <p className="md:text-xl text-gray-600 max-w-3xl mx-auto">
                The most accurate AI hiring platform with proven ROI and
                enterprise-grade security
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow "
                >
                  <div className="text-purple-600 mb-4 flex justify-center items-center">
                    {feature.icon}
                    <h3 className="text-lg font-bold text-gray-900 mb-2 mx-4">
                      {feature.title}
                    </h3>
                  </div>

                  <p className="text-gray-600 text-center">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <div className="w-full mx-auto max-md:mt-10 overflow-x-auto md:px-52 lg:px-20 max-md:bg-white bg-[#fbfbfb] max-md:pt-2 max-md:pb-2 pt-4 pb-16">
          <div className="flex items-center flex-col">
            <h2 className="text-4xl max-md:text-2xl text-center font-bold max-md:font-bold mb-4">
              Compare All Features
            </h2>
            <p className="text-lg text-gray-600 mb-4 text-center max-w-3xl">
              See exactly what's included in each plan
            </p>
          </div>

          <ComparisonTable />
        </div>

        {/* Modals */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6 pt-8 animate-scale-in">
              <button
                className="absolute top-1 right-3 text-black hover:text-gray-600 text-2xl"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                &times;
              </button>

              <div className="flex justify-center mb-4">
                <img
                  src="https://cdn-icons-png.flaticon.com/512/10365/10365386.png"
                  alt="Payment Icon"
                  className="w-24 h-24"
                />
              </div>

              <div className="text-center">
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                  Payment Gateway Page
                </h2>
                <p className="text-base text-gray-600 mt-4 mb-4">
                  Will be active once Payment Gateway is live
                </p>
              </div>
            </div>
          </div>
        )}

        {showModalSales && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-5 md:px-0">
            <div className="relative w-full max-w-2xl">
              <button
                className="absolute top-2 right-3 text-black hover:text-gray-600 text-2xl z-10"
                onClick={closeModalSale}
                aria-label="Close"
              >
                &times;
              </button>
              <SalesData closeModalSale={closeModalSale} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
